// Reading a place out of a pasted map link, with no API key (docs/research/ORGANISER_RESEARCH.md §5).
// Long Google Maps links carry the name and the pin in the URL itself; short ones
// (maps.app.goo.gl) are expanded by the worker, which follows the redirect and parses the result.
// Apple Maps links, geo: URIs and plain "lat, lng" text work too, for trips outside Japan.

export interface MapLink {
  name?: string
  lat?: number
  lng?: number
  /** Where the coordinates came from: the place's own pin is better than the view's centre. */
  precision?: 'pin' | 'view' | 'query'
}

const SHORT_HOSTS = ['maps.app.goo.gl', 'goo.gl']

/** A short link that needs the worker to expand it. */
export function isShortMapLink(text: string): boolean {
  try {
    const u = new URL(text.trim())
    return SHORT_HOSTS.includes(u.hostname) && (u.hostname !== 'goo.gl' || u.pathname.startsWith('/maps'))
  } catch {
    return false
  }
}

const okLat = (n: number) => Number.isFinite(n) && Math.abs(n) <= 90
const okLng = (n: number) => Number.isFinite(n) && Math.abs(n) <= 180
const LATLNG = /(-?\d{1,2}(?:\.\d+)?)\s*,\s*(-?\d{1,3}(?:\.\d+)?)/

function latLng(text: string): { lat: number; lng: number } | null {
  const m = LATLNG.exec(text)
  if (!m) return null
  const lat = Number(m[1])
  const lng = Number(m[2])
  return okLat(lat) && okLng(lng) ? { lat, lng } : null
}

const words = (s: string) => decodeURIComponent(s.replace(/\+/g, ' ')).trim()

/** Parses a map link (or "lat, lng" text). Returns null if there's nothing usable in it. */
export function parseMapLink(text: string): MapLink | null {
  const raw = text.trim()
  if (!raw) return null

  if (raw.startsWith('geo:')) {
    const at = latLng(raw.slice(4))
    const q = /[?&]q=([^&]+)/.exec(raw)?.[1]
    const name = q ? words(q).replace(/^-?[\d.]+\s*,\s*-?[\d.]+\s*/, '').replace(/^\((.*)\)$/, '$1') || undefined : undefined
    return at ? { ...at, name, precision: 'pin' } : null
  }

  let url: URL
  try {
    url = new URL(raw)
  } catch {
    const at = latLng(raw)
    return at && raw.replace(LATLNG, '').trim() === '' ? { ...at, precision: 'pin' } : null
  }

  const out: MapLink = {}
  const host = url.hostname.replace(/^www\./, '')
  const full = `${url.pathname}${url.search}${url.hash}`

  if (host === 'maps.apple.com') {
    const ll = url.searchParams.get('ll') ?? url.searchParams.get('sll') ?? url.searchParams.get('coordinate')
    const at = ll ? latLng(ll) : null
    if (at) Object.assign(out, at, { precision: 'pin' })
    const q = url.searchParams.get('q') ?? url.searchParams.get('name')
    if (q) out.name = q.trim()
    return out.name || out.lat !== undefined ? out : null
  }

  if (!/(^|\.)google\.[a-z.]+$/.test(host) && !host.startsWith('maps.google.')) return null

  // 1. The place's own pin: …!3d43.0618!4d141.3545…
  const pin = /!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/.exec(full)
  if (pin && okLat(Number(pin[1])) && okLng(Number(pin[2]))) Object.assign(out, { lat: Number(pin[1]), lng: Number(pin[2]), precision: 'pin' })
  // 2. The view's centre: …/@43.0618,141.3545,17z
  if (out.lat === undefined) {
    const view = /@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/.exec(url.pathname)
    if (view && okLat(Number(view[1])) && okLng(Number(view[2]))) Object.assign(out, { lat: Number(view[1]), lng: Number(view[2]), precision: 'view' })
  }
  // 3. A query: ?q=… or ?query=… (a name, or coordinates).
  const q = url.searchParams.get('q') ?? url.searchParams.get('query') ?? url.searchParams.get('destination')
  if (q) {
    const at = latLng(q)
    if (at && out.lat === undefined) Object.assign(out, at, { precision: 'query' })
    else if (!at) out.name = q.trim()
  }
  // 4. The name: /maps/place/Sapporo+TV+Tower/…
  const place = /\/maps\/place\/([^/@]+)/.exec(url.pathname)
  if (place) out.name = words(place[1]!)

  return out.name || out.lat !== undefined ? out : null
}
